// Module « Livret de l'année » — agrège le suivi de l'année (présences, appréciations des
// tuteurs, projet technique, documents) et produit un livret imprimable.
//
// Retrait facile (si jugé trop sensible) : supprimer la ligne « livret » de Sidebar.jsx et
// la ligne « view === "livret" » de ProfessorDashboard.jsx ; ce fichier peut alors être
// supprimé — les champs livret_* restent au repos dans les fiches (inertes), et le bloc
// de règles App.css « impression-livret » peut être retiré. Aucun autre écran n'en dépend.
import { useEffect, useMemo, useState } from "react";
import { BookOpen, Printer, Save, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getEtablissementSettings, loadDashboard, updateEtablissementSettings, updateStudent } from "@/lib/api";
import { byId, formatDate, fullName } from "@/lib/helpers";

const docsSuivi = [
  ["convention_signee", "convention_signee_date", "Convention de stage signée"],
  ["livret_stages_remis", "livret_stages_remis_date", "Livret de stages remis"],
  ["visite_stage_effectuee", "visite_stage_effectuee_date", "Visite de stage effectuée"],
  ["attestation_stage_recue", "attestation_stage_recue_date", "Attestation de stage reçue"],
  ["certificat_stage_1_recu", "certificat_stage_1_recu_date", "Certificat de stage n°1 reçu"],
  ["certificat_stage_2_recu", "certificat_stage_2_recu_date", "Certificat de stage n°2 reçu"],
  ["evaluation_stagiaire_recue", "evaluation_stagiaire_recue_date", "Évaluation du stagiaire reçue"],
];

const vigilanceBehaviors = ["Difficultés de compréhension", "Manque d'initiative", "Difficulté relationnelle", "Situation à signaler au professeur référent"];

const tirets = "__________________________________________________________________";

export default function LivretAnnuel({ user }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [reglagesOuverts, setReglagesOuverts] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadDashboard().then((result) => {
      setData(result);
      setSelectedId((current) => current || result.students?.[0]?.id || "");
    }).catch(() => setMessage("Impossible de charger les données."));
    getEtablissementSettings().then(setSettings).catch(() => {});
  }, []);

  const students = data?.students || [];
  const student = students.find((item) => item.id === selectedId) || null;
  const company = useMemo(() => {
    if (!student || !data) return null;
    const internship = (data.internships || []).find((item) => item.student_id === student.id);
    return internship ? byId(data.companies || [])[internship.company_id] : null;
  }, [data, student]);
  const internship = useMemo(() => data && student ? (data.internships || []).find((item) => item.student_id === student.id) : null, [data, student]);

  useEffect(() => {
    if (!student) return;
    setForm({
      livret_appreciation_generale: student.livret_appreciation_generale || "",
      livret_bilan_parcours: student.livret_bilan_parcours || "",
      livret_avis_poursuite: student.livret_avis_poursuite || "",
      livret_observations: student.livret_observations || "",
      livret_date_signature: student.livret_date_signature || "",
    });
    setMessage("");
  }, [student]);

  const agregat = useMemo(() => {
    if (!data || !student) return null;
    const atts = (data.attendances || []).filter((item) => item.student_id === student.id).sort((a, b) => a.date.localeCompare(b.date));
    const presents = atts.filter((item) => item.status === "present");
    const absences = atts.filter((item) => item.status === "absent");
    const retards = atts.filter((item) => item.presence_type === "Arrivée tardive");
    const departs = atts.filter((item) => item.presence_type === "Départ anticipé");
    const vigilance = [];
    for (const item of atts) {
      for (const point of item.comportement || []) {
        if (vigilanceBehaviors.includes(point) && !vigilance.includes(point)) vigilance.push(point);
      }
    }
    const appreciations = atts.filter((item) => item.appreciation).map((item) => ({ date: item.date, valeur: item.appreciation }));
    const notes = atts.filter((item) => item.note && item.note.trim()).slice(-3).map((item) => ({ date: item.date, valeur: item.note.trim() }));
    const taux = atts.length ? Math.round((presents.length / atts.length) * 100) : null;
    return { atts, presents, absences, retards, departs, vigilance, appreciations, notes, taux };
  }, [data, student]);

  const anneeScolaire = useMemo(() => {
    const jour = data?.server_today || new Date().toISOString().slice(0, 10);
    const annee = Number(jour.slice(0, 4));
    const mois = Number(jour.slice(5, 7));
    return mois >= 8 ? `${annee}-${annee + 1}` : `${annee - 1}-${annee}`;
  }, [data]);

  const enregistrerSynthese = async () => {
    if (!student) return;
    setMessage("Enregistrement…");
    try {
      await updateStudent(student.id, { ...student, ...form });
      setData((current) => current ? { ...current, students: current.students.map((item) => item.id === student.id ? { ...item, ...form } : item) } : current);
      setMessage("Synthèse enregistrée.");
    } catch (err) {
      setMessage(err.response?.data?.detail || "Enregistrement impossible.");
    }
  };

  const copierSynthese = async () => {
    if (!student) return;
    const texte = [
      `${fullName(student)} — ${student.class_name}`,
      "",
      `Appréciation générale : ${form.livret_appreciation_generale || "—"}`,
      `Bilan du parcours en entreprise : ${form.livret_bilan_parcours || "—"}`,
      `Avis sur la poursuite d'études : ${form.livret_avis_poursuite || "—"}`,
      `Observations : ${form.livret_observations || "—"}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(texte);
      setMessage("Synthèse copiée — collez-la dans la rubrique Pronote (fiche de stage ou bulletin).");
    } catch (err) {
      setMessage("Copie automatique impossible dans ce navigateur : sélectionnez le texte du livret à la main.");
    }
  };

  const enregistrerReglages = async (event) => {
    event.preventDefault();
    const donnees = new FormData(event.currentTarget);
    const payload = {
      nom_etablissement: String(donnees.get("nom_etablissement") || "").trim(),
      adresse_etablissement: String(donnees.get("adresse_etablissement") || "").trim(),
      entete_republique: donnees.get("entete_republique") === "on",
    };
    try {
      const resultat = await updateEtablissementSettings(payload);
      setSettings(resultat);
      setReglagesOuverts(false);
      setMessage("Paramètres de l'établissement enregistrés.");
    } catch (err) {
      setMessage(err.response?.data?.detail || "Enregistrement des paramètres impossible (réservé aux administrateurs).");
    }
  };

  const imprimer = () => {
    document.body.classList.add("impression-livret");
    window.print();
    setTimeout(() => document.body.classList.remove("impression-livret"), 600);
  };

  if (!data) return <section className="p-6 text-zinc-500" data-testid="livret-loading">Chargement du livret…</section>;

  return (
    <section className="space-y-5" data-testid="livret-view">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#002FA7]" data-testid="livret-kicker">Fin d'année</p>
          <h2 className="mt-2 font-display text-3xl font-bold" data-testid="livret-title">Livret de l'année</h2>
          <p className="mt-2 max-w-3xl text-zinc-600" data-testid="livret-description">Le livret rassemble automatiquement la matière de l'année — présences, appréciations des tuteurs, projet technique, documents — puis vos champs de synthèse. Imprimez-le pour l'archiver ou le transmettre.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="livret-actions">
          <Button variant="outline" className="rounded-none" onClick={() => setReglagesOuverts((v) => !v)} data-testid="livret-settings-toggle"><Settings className="h-4 w-4" />Paramètres établissement</Button>
          <Button className="rounded-none bg-[#002FA7]" onClick={imprimer} disabled={!student} data-testid="livret-print-button"><Printer className="h-4 w-4" />Imprimer le livret</Button>
        </div>
      </div>

      {reglagesOuverts && settings && (
        <form onSubmit={enregistrerReglages} className="grid gap-3 border border-zinc-200 bg-white p-4 md:grid-cols-2" data-testid="livret-settings">
          <div className="space-y-2"><Label htmlFor="livret-nom-etablissement">Nom de l'établissement</Label><Input id="livret-nom-etablissement" name="nom_etablissement" defaultValue={settings.nom_etablissement} className="rounded-none" data-testid="livret-settings-nom" /></div>
          <div className="space-y-2"><Label htmlFor="livret-adresse-etablissement">Adresse / académie</Label><Input id="livret-adresse-etablissement" name="adresse_etablissement" defaultValue={settings.adresse_etablissement} className="rounded-none" data-testid="livret-settings-adresse" /></div>
          <label className="flex items-center gap-3 text-sm font-semibold md:col-span-2" data-testid="livret-settings-republique-label"><input type="checkbox" name="entete_republique" defaultChecked={settings.entete_republique} data-testid="livret-settings-republique" /> Afficher l'en-tête « République française » sur le livret — option verrouillée, à n'activer qu'avec l'accord du chef d'établissement (voir note ci-dessous)</label>
          <p className="text-xs text-zinc-500 md:col-span-2" data-testid="livret-settings-avertissement">L'emblème de l'État identifie une communication émanant de ses services : l'émetteur légitime du livret est l'établissement, pas l'application. Tant qu'il n'a pas donné son accord écrit, laissez cette option désactivée.</p>
          <div className="md:col-span-2"><Button type="submit" className="rounded-none" data-testid="livret-settings-save"><Save className="h-4 w-4" />Enregistrer les paramètres</Button></div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3" data-testid="livret-controls">
        <Label htmlFor="livret-student-select" className="text-sm font-semibold">Étudiant :</Label>
        <select id="livret-student-select" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} className="h-10 min-w-[260px] border border-zinc-300 bg-white px-3" data-testid="livret-student-select">
          {students.map((item) => <option key={item.id} value={item.id}>{fullName(item)} — {item.class_name}</option>)}
        </select>
        {message && <span className="text-sm text-zinc-600" data-testid="livret-message">{message}</span>}
      </div>

      {student && agregat && (
        <>
          <div className="grid gap-4 border border-zinc-200 bg-white p-4 lg:grid-cols-2" data-testid="livret-editeur">
            <div className="space-y-2"><Label htmlFor="livret-appreciation">Appréciation générale</Label><Textarea id="livret-appreciation" value={form.livret_appreciation_generale || ""} onChange={(event) => setForm({ ...form, livret_appreciation_generale: event.target.value })} className="rounded-none" data-testid="livret-appreciation-input" /><p className={`text-xs ${(form.livret_appreciation_generale || "").length > 1000 ? "font-semibold text-red-700" : "text-zinc-500"}`} data-testid="livret-appreciation-compteur">{(form.livret_appreciation_generale || "").length} / 1 000 caractères — format attendu pour la synthèse PFMP du livret scolaire</p></div>
            <div className="space-y-2"><Label htmlFor="livret-bilan">Bilan du parcours en entreprise</Label><Textarea id="livret-bilan" value={form.livret_bilan_parcours || ""} onChange={(event) => setForm({ ...form, livret_bilan_parcours: event.target.value })} className="rounded-none" data-testid="livret-bilan-input" /></div>
            <div className="space-y-2"><Label htmlFor="livret-avis">Avis sur la poursuite d'études / l'orientation</Label><Textarea id="livret-avis" value={form.livret_avis_poursuite || ""} onChange={(event) => setForm({ ...form, livret_avis_poursuite: event.target.value })} className="rounded-none" data-testid="livret-avis-input" /></div>
            <div className="space-y-2"><Label htmlFor="livret-observations">Observations complémentaires</Label><Textarea id="livret-observations" value={form.livret_observations || ""} onChange={(event) => setForm({ ...form, livret_observations: event.target.value })} className="rounded-none" data-testid="livret-observations-input" /></div>
            <div className="space-y-2"><Label htmlFor="livret-date-signature">Date de signature</Label><Input id="livret-date-signature" type="date" value={form.livret_date_signature || ""} onChange={(event) => setForm({ ...form, livret_date_signature: event.target.value })} className="rounded-none" data-testid="livret-date-input" /></div>
            <div className="flex items-end gap-2"><Button onClick={enregistrerSynthese} className="rounded-none bg-[#002FA7]" data-testid="livret-save-button"><Save className="h-4 w-4" />Enregistrer la synthèse</Button><Button variant="outline" className="rounded-none" onClick={copierSynthese} data-testid="livret-copy-button">Copier la synthèse</Button></div>
            <p className="text-xs text-zinc-500 md:col-span-2" data-testid="livret-editeur-note">À rédiger comme sur un bulletin : uniquement le parcours en entreprise et la scolarité. Ne mentionnez ni données de santé, ni éléments de vie privée.</p>
          </div>

          <article className="mx-auto w-full max-w-4xl border border-zinc-300 bg-white p-8" data-testid="livret-sheet">
            <header className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-[#002FA7] pb-4">
              {settings?.entete_republique && <div data-testid="livret-entete-republique"><p className="font-display text-sm font-bold uppercase tracking-[0.24em] text-zinc-800">République française</p><p className="mt-1 text-xs text-zinc-500">Ministère de l'Éducation nationale</p></div>}
              <div className="text-right" data-testid="livret-entete-etablissement"><p className="font-display text-lg font-bold">{settings?.nom_etablissement || "Établissement"}</p><p className="text-xs text-zinc-500">{settings?.adresse_etablissement || ""}</p></div>
            </header>

            <h3 className="mt-5 font-display text-2xl font-bold" data-testid="livret-sheet-title">Livret de suivi — période de formation en milieu professionnel et parcours en entreprise</h3>
            <p className="mt-1 text-sm text-zinc-500">Année scolaire {anneeScolaire}</p>

            <section className="mt-6" data-testid="livret-section-identification">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">1. Identification</h4>
              <div className="mt-2 grid gap-1 text-sm md:grid-cols-2">
                <p><span className="font-semibold">Étudiant :</span> {fullName(student)}</p>
                <p><span className="font-semibold">Classe :</span> {student.class_name} {student.annee ? `(${student.annee})` : ""}</p>
                <p><span className="font-semibold">Né(e) le :</span> {student.date_naissance ? formatDate(student.date_naissance) : "—"}</p>
                <p><span className="font-semibold">Référent pédagogique :</span> {student.referent_pedagogique_nom || "—"}</p>
                <p><span className="font-semibold">Entreprise :</span> {company?.name || "—"}</p>
                <p><span className="font-semibold">Tuteur :</span> {company?.tutor_name || "—"}</p>
                {internship && <p className="md:col-span-2"><span className="font-semibold">Période :</span> du {formatDate(internship.start_date)} au {formatDate(internship.end_date)}</p>}
              </div>
            </section>

            <section className="mt-5" data-testid="livret-section-assiduite">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">2. Assiduité (synthèse des confirmations)</h4>
              <p className="mt-2 text-sm">Jours de présence confirmée par le tuteur : {agregat.presents.length} — absences signalées : {agregat.absences.length}{agregat.taux !== null ? ` — taux de présence : ${agregat.taux} %` : ""} (la durée effective se compte en jours ; règle de référence : 7 heures = 1 jour).</p>
              {(agregat.retards.length > 0 || agregat.departs.length > 0 || agregat.absences.length > 0) && (
                <ul className="mt-2 list-disc pl-5 text-sm">
                  {agregat.retards.map((item) => <li key={`r-${item.id}`}>Arrivée tardive le {formatDate(item.date)} (confirmé par {item.confirmed_by}).</li>)}
                  {agregat.departs.map((item) => <li key={`d-${item.id}`}>Départ anticipé le {formatDate(item.date)} (confirmé par {item.confirmed_by}).</li>)}
                  {agregat.absences.map((item) => <li key={`a-${item.id}`}>Absence signalée le {formatDate(item.date)} ({item.note || "sans précision"}).</li>)}
                </ul>
              )}
            </section>

            <section className="mt-5" data-testid="livret-section-entreprise">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">3. Suivi en entreprise (PFMP)</h4>
              {agregat.appreciations.length > 0 && <ul className="mt-2 list-disc pl-5 text-sm">{agregat.appreciations.map((item) => <li key={`ap-${item.date}`}>Appréciation du tuteur du {formatDate(item.date)} : {item.valeur}.</li>)}</ul>}
              {agregat.vigilance.length > 0 && <p className="mt-2 text-sm"><span className="font-semibold">Points de vigilance relevés :</span> {agregat.vigilance.join(" ; ")}.</p>}
              {agregat.notes.length > 0 && <ul className="mt-2 list-disc pl-5 text-sm">{agregat.notes.map((item) => <li key={`n-${item.date}`}>Observation du {formatDate(item.date)} : {item.valeur}</li>)}</ul>}
              {agregat.appreciations.length === 0 && agregat.notes.length === 0 && <p className="mt-2 text-sm text-zinc-500">Aucune appréciation du tuteur enregistrée.</p>}
              <p className="mt-3 text-sm">Visa du tuteur en entreprise (nom, date, signature) : {tirets}</p>
            </section>

            <section className="mt-5" data-testid="livret-section-projet">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">4. Projet technique</h4>
              <p className="mt-2 text-sm"><span className="font-semibold">Thème validé :</span> {student.projet_technique_theme || tirets}</p>
              {student.projet_technique_remarque_tuteur && <p className="mt-1 text-sm"><span className="font-semibold">Remarque du tuteur :</span> {student.projet_technique_remarque_tuteur}</p>}
            </section>

            <section className="mt-5" data-testid="livret-section-documents">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">5. Documents administratifs</h4>
              <ul className="mt-2 grid gap-1 text-sm md:grid-cols-2">
                {docsSuivi.map(([cle, cleDate, libelle]) => <li key={cle}>{student[cle] ? "✔" : "☐"} {libelle}{student[cleDate] ? ` — ${formatDate(student[cleDate])}` : ""}</li>)}
              </ul>
            </section>

            <section className="mt-5" data-testid="livret-section-synthese">
              <h4 className="border-b border-zinc-300 pb-1 font-display text-lg font-bold">6. Synthèse du professeur référent</h4>
              <p className="mt-2 text-sm"><span className="font-semibold">Appréciation générale :</span> {form.livret_appreciation_generale || tirets}</p>
              <p className="mt-1 text-sm"><span className="font-semibold">Bilan du parcours en entreprise :</span> {form.livret_bilan_parcours || tirets}</p>
              <p className="mt-1 text-sm"><span className="font-semibold">Avis sur la poursuite d'études / l'orientation :</span> {form.livret_avis_poursuite || tirets}</p>
              <p className="mt-1 text-sm"><span className="font-semibold">Observations :</span> {form.livret_observations || tirets}</p>
              <p className="mt-3 text-sm">Fait à {settings?.adresse_etablissement?.split("—").pop()?.trim() || "…"}, le {form.livret_date_signature ? formatDate(form.livret_date_signature) : tirets}</p>
              <p className="mt-2 text-sm">Le professeur référent (nom, signature) : {tirets}</p>
            </section>

            <footer className="mt-6 border-t border-zinc-300 pt-2 text-xs text-zinc-500" data-testid="livret-sheet-footer">
              Document établi avec l'application FilStage — données d'évaluation et de suivi de la période en milieu professionnel{settings?.nom_etablissement ? ` — ${settings.nom_etablissement}` : ""}.
            </footer>
          </article>
        </>
      )}
    </section>
  );
}
